<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row justify-content-center">
        <div class="col col-md-10 col-lg-10">
          <h1 class="text-center">Registration</h1>
          <ErrorSummary :error="error" />
          <div class="row mt-4 d-print-none">
            <div class="col">
              <button
                type="button"
                class="btn btn-block"
                :class="{ 'btn-primary': isSingleMode, 'btn-outline-primary': !isSingleMode }"
                @click="mode = 'single'"
                data-bs-toggle="tooltip"
                data-bs-delay="500"
                title="Create a single Interview"
              >
                Single Interview
              </button>
            </div>
            <div class="col">
              <button
                type="button"
                class="btn btn-block"
                :class="{ 'btn-primary': isBulkMode, 'btn-outline-primary': !isBulkMode }"
                @click="mode = 'bulk'"
                data-bs-toggle="tooltip"
                data-bs-delay="500"
                title="Bulk create interviews"
              >
                Bulk Interviews
              </button>
            </div>
          </div>
          <div v-if="isSingleMode">
            <div class="card my-4" v-if="showDetailsForm">
              <h5 class="card-header">Details</h5>
              <div class="card-body">
                <form ref="formDetails" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
                  <div class="form-group">
                    <label>Registration ID</label>
                    <input
                      v-model="registrationId"
                      type="text"
                      class="form-control"
                      :disabled="loadingMessage"
                      required
                      @keyup.enter="next"
                      @blur="getReportRegistrationIdDuplicate"
                      placeholder="Registration Id"
                      data-bs-toggle="tooltip"
                      data-bs-delay="500"
                      title="Insert a Registration ID, which needs to be a unique alphanumeric value for each interviewee"
                    />
                    <div class="invalid-feedback">Please provide a registration ID.</div>
                  </div>
                  <div class="form-group">
                    <label>Interview Type</label>
                    <select v-model="testId" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select an interview type</option>
                      <option v-for="t in tests" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a test type.</div>
                  </div>
                  <div v-if="showInvestigationId" class="form-group">
                    <label>Investigation ID</label>
                    <input
                      v-model="investigationId"
                      type="text"
                      class="form-control"
                      :disabled="loadingMessage"
                      required
                      @keyup.enter="next"
                      placeholder="Investigation Id"
                      data-bs-toggle="tooltip"
                      data-bs-delay="500"
                      title="Unique alphanumeric value for an investigation"
                    />
                    <div class="invalid-feedback">Please provide an investigation ID.</div>
                  </div>
                  <div class="form-group">
                    <label>Country</label>
                    <select v-model="country" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select a country</option>
                      <option :value="c.code" v-for="c in countries" :key="c.code">
                        {{ c.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a country.</div>
                  </div>
                  <div class="form-group" v-if="country">
                    <label>State</label>
                    <select v-model="state" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select a state</option>
                      <option :value="s.code" v-for="s in countryStates" :key="s.code">
                        {{ s.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a state.</div>
                  </div>
                  <div class="form-group">
                    <label>Function/District</label>
                    <select v-model="functionId" class="form-control" :disabled="loadingMessage || functionsDisabled" :required="!functionsDisabled">
                      <option value selected hidden disabled>{{ functionsDisabled ? "N/A" : "Select a function/district" }}</option>
                      <option v-for="f in functions" v-bind:key="f.id" v-bind:value="f.id">
                        {{ f.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a function/district.</div>
                  </div>
                  <div class="form-group">
                    <label>Region</label>
                    <select v-model="regionId" class="form-control" :disabled="loadingMessage || regionsDisabled" :required="!regionsDisabled">
                      <option value selected hidden disabled>{{ regionsDisabled ? "N/A" : "Select a region" }}</option>
                      <option v-for="r in regions" v-bind:key="r.id" v-bind:value="r.id">
                        {{ r.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a region.</div>
                  </div>
                  <div class="form-group">
                    <label>Facility/Store</label>
                    <select v-model="facilityId" class="form-control" :disabled="loadingMessage || facilitiesDisabled" :required="!facilitiesDisabled">
                      <option value selected hidden disabled>{{ facilitiesDisabled ? "N/A" : "Select a facility/store" }}</option>
                      <option v-for="f in facilities" v-bind:key="f.id" v-bind:value="f.id">
                        {{ f.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a facility/store.</div>
                  </div>
                  <div class="form-group">
                    <label>Company/Division</label>
                    <select v-model="companyId" class="form-control" :disabled="loadingMessage || companiesDisabled" :required="!companiesDisabled">
                      <option value selected hidden disabled>{{ companiesDisabled ? "N/A" : "Select a company/division" }}</option>
                      <option v-for="f in companies" v-bind:key="f.id" v-bind:value="f.id">
                        {{ f.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a company/division.</div>
                  </div>
                  <div class="form-group">
                    <div
                      class="form-check-inline"
                      data-bs-toggle="tooltip"
                      data-bs-delay="500"
                      title="Checking this box will provide the interviewee with an accomodation of extra time to answer each question."
                    >
                      <label class="form-check-label" for="extendedTime">
                        Extended Time
                      </label>
                      <input v-model="extendedTime" id="extendedTime" class="form-check-input m-2" type="checkbox" :disabled="loadingMessage" />
                    </div>
                  </div>
                  <div v-if="extendedTime" class="form-group">
                    <input
                      v-model="extendedTimeReason"
                      type="text"
                      id="extendedTimeReason"
                      class="form-control"
                      placeholder="Reason for Extension"
                      :disabled="loadingMessage"
                      required
                      @keyup.enter="next"
                    />
                    <div class="invalid-feedback">Please provide a reason for extended time.</div>
                  </div>
                  <div class="form-group">
                    <label>Language</label>
                    <select v-model="languageCode" class="form-control" :disabled="loadingMessage" required @keyup.enter="next">
                      <option v-for="f in languages" v-bind:key="f.id" v-bind:value="f.id">
                        {{ f.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a language.</div>
                  </div>
                  <div
                    class="modal fade"
                    id="duplicateRegistrationModal"
                    data-backdrop="static"
                    tabindex="-1"
                    role="dialog"
                    aria-labelledby="duplicateRegistrationLabel"
                    aria-hidden="true"
                  >
                    <div class="modal-dialog" role="document">
                      <div class="modal-content">
                        <div class="modal-header">
                          <h5 class="modal-title" id="duplicateRegistrationLabel">Warning: Duplicate Registration ID Detected</h5>
                          <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                          </button>
                        </div>
                        <div class="modal-body">
                          <p>
                            Please note that the Registration ID you have entered to create this test has previously been used by this organization on
                            {{ formatter.date.short(this.previousDate) }}.
                          </p>
                          <p>
                            If you wish to proceed to create this test, please click Continue below. Otherwise, please provide a different Registration ID.
                          </p>
                        </div>
                        <div class="modal-footer">
                          <button type="button" class="btn btn-primary" data-dismiss="modal">Close</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div class="card-footer">
                <div class="btn-group d-flex justify-content-around" role="group">
                  <button type="button" class="btn btn-primary w-50 mr-2" @click="next" :disabled="loadingMessage" @keyup.enter="next">
                    <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ loadingMessage ? loadingMessage : "Continue" }}
                  </button>
                  <button type="button" class="btn btn-primary w-50 ml-2" @click="cancel" :disabled="loadingMessage">Cancel</button>
                </div>
              </div>
            </div>
            <div class="card" v-if="showDemographicsForm">
              <h5 class="card-header">Demographic Data</h5>
              <div class="card-body">
                <form ref="formDemographics" class="needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
                  <div class="form-group">
                    <label>Race</label>
                    <select v-model="raceCode" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select a race</option>
                      <option v-for="t in races" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a race.</div>
                  </div>
                  <div class="form-group">
                    <label>Age Group</label>
                    <select v-model="ageGroupCode" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select an age group</option>
                      <option v-for="t in ageGroups" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select an age group.</div>
                  </div>
                  <div class="form-group">
                    <label>Gender</label>
                    <select v-model="genderCode" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select a gender</option>
                      <option v-for="t in genders" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select a gender.</div>
                  </div>
                  <div class="form-group">
                    <label>Education Level</label>
                    <select v-model="educationLevelCode" class="form-control" :disabled="loadingMessage" required>
                      <option value selected hidden disabled>Select an education level</option>
                      <option v-for="t in educationLevels" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select an education level.</div>
                  </div>
                  <div class="form-group">
                    <label>Employment Status</label>
                    <select v-model="employmentStatusCode" class="form-control" :disabled="loadingMessage" required @keyup.enter="next">
                      <option value selected hidden disabled>Select an employment status</option>
                      <option v-for="t in employmentStatuses" v-bind:key="t.id" v-bind:value="t.id">
                        {{ t.displayName }}
                      </option>
                    </select>
                    <div class="invalid-feedback">Please select an employment status.</div>
                  </div>
                </form>
              </div>
              <div class="card-footer">
                <div class="btn-group d-flex justify-content-around" role="group">
                  <button type="button" class="btn btn-primary w-50 mr-2" @click="prev" :disabled="loadingMessage" @keyup.enter="prev">Back</button>
                  <button type="button" class="btn btn-primary w-50 mr-2" @click="next" :disabled="loadingMessage" @keyup.enter="next">
                    <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ loadingMessage ? loadingMessage : "Continue" }}
                  </button>
                  <button type="button" class="btn btn-primary w-50 ml-2" @click="cancel" :disabled="loadingMessage">Cancel</button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="isBulkMode">
            <div class="card my-4">
              <h5 class="card-header">Bulk</h5>
              <div class="card-body">
                <div class="row" v-if="!hasBulkData">
                  <div class="col">
                    <div class="alert alert-info" role="alert">
                      <h5 class="alert-heading">Instructions</h5>
                      <p>Select an Excel or CSV file using the file upload dialog below.</p>
                      <hr />
                      <p>
                        <span class="span-link" @click="downloadSampleFile"><fa icon="file-excel" fixed-width /> Download our sample file</span> and use it as a
                        template.
                      </p>
                    </div>
                    <div v-if="isBadFile" class="alert alert-warning" role="alert">
                      <fa icon="exclamation-triangle" fixed-width /> <strong>Warning</strong> This file doesn't match our import format. Please update your file
                      and try again.
                    </div>
                    <form ref="form" class="p-2" novalidate @submit.prevent>
                      <div class="form-group">
                        <input ref="fileUpload" type="file" accept=".xls,.xlsx,.csv" class="form-control-file" @change.prevent="onFile" />
                      </div>
                    </form>
                  </div>
                </div>
                <div class="row" v-if="hasBulkData">
                  <div class="col">
                    <ErrorSummary :error="error" />
                    <p>
                      Below is a preview of your data. While you review, we'll check for duplicates. When you're ready, click the
                      <strong>Import</strong> button below to complete the import process.
                    </p>
                    <div v-if="isValidating" class="alert alert-info" role="alert">
                      <fa icon="sync" fixed-width spin /> <strong>Validating</strong> {{ totalRowsValidated }} of {{ totalRows }} rows ...
                    </div>
                    <div v-if="hasInvalidRows" class="alert alert-danger" role="alert">
                      <fa icon="exclamation-triangle" fixed-width /> We detected one or more invalid rows.
                      <hr />
                      These rows must be corrected before they can be imported.
                      <span class="span-link" @click="resetBulk">Click here to upload an updated file.</span>
                    </div>
                    <div v-if="hasDuplicates" class="alert alert-warning" role="alert">
                      <fa icon="exclamation-triangle" fixed-width /> We detected one or more duplicate Registration IDs. If you would like to change the
                      duplicate Registration IDs before import, <span class="span-link" @click="resetBulk">click here to upload an updated file.</span>
                      <hr />
                      <ul class="list-unstyled">
                        <li>Duplicate Registration IDs (<fa icon="exclamation-triangle" fixed-width />) are accepted.</li>
                      </ul>
                    </div>
                    <div class="table-wrap my-2">
                      <table class="table">
                        <thead>
                          <tr>
                            <th scope="col">Validation Status</th>
                            <th scope="col">Registration Id</th>
                            <th scope="col">Email Address</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="i in fileUploadData" :key="i.key">
                            <td>
                              <fa :icon="getRowStatusIcon(i)" fixed-width :spin="getRowStatusIconSpin(i)" :class="getRowStatusIconClass(i)" />
                              <span class="d-none d-lg-inline">{{ getRowStatusText(i) }}</span>
                            </td>
                            <td :class="getRowClass(i)">{{ i.registrationId || "---" }}</td>
                            <td :class="getRowClass(i)">{{ i.emailAddress || "---" }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
              <div class="card-footer">
                <div class="btn-group d-flex justify-content-around" role="group">
                  <button
                    type="button"
                    class="btn btn-primary w-50 mr-2"
                    @click="submitBulk"
                    :disabled="loadingMessage || hasInvalidRows"
                    @keyup.enter="submitBulk"
                  >
                    <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ loadingMessage ? loadingMessage : "Continue" }}
                  </button>
                  <button type="button" class="btn btn-primary w-50 ml-2" @click="cancelBulk" :disabled="loadingMessage">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import NavBar from "@/views/reports/NavBar.vue";
import ErrorSummary from "@/components/ErrorSummary.vue";
import formatter from "@/utils/formatter";
import $ from "jquery";
import XLSX from "xlsx";

export default {
  components: {
    NavBar,
    ErrorSummary,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      mode: "single",
      isBadFile: false,
      fileUploadData: [],

      // wizard
      formWizardStep: 1,
      collectDemographicData: false,

      // details
      companies: [],
      companyId: "",
      facilities: [],
      facilityId: "",
      regions: [],
      regionId: "",
      functions: [],
      functionId: "",
      tests: [],
      languages: [
        { displayName: "English", id: "en" },
        { displayName: "Spanish", id: "es" },
      ],
      testId: "",
      registrationId: null,
      investigationId: null,
      country: "",
      state: "",
      extendedTime: false,
      extendedTimeReason: null,
      languageCode: "en",

      // demographics
      races: [],
      raceCode: "",
      ageGroups: [],
      ageGroupCode: "",
      genders: [],
      genderCode: "",
      educationLevels: [],
      educationLevelCode: "",
      employmentStatuses: [],
      employmentStatusCode: "",

      // previously used
      previousDate: null,
    };
  },
  watch: {
    country() {
      this.state = "";
    },
    async fileUploadData(newValue, oldValue) {
      if (newValue && newValue.length && oldValue && !oldValue.length) {
        await this.validateFile();
      }
    },
  },
  computed: {
    ...mapGetters("user", ["user"]),
    ...mapGetters("static", ["countries", "states"]),
    isSingleMode() {
      return this.mode == "single";
    },
    isBulkMode() {
      return this.mode == "bulk";
    },
    hasBulkData() {
      return this.fileUploadData && this.fileUploadData.length;
    },
    isValidating() {
      return this.totalRows !== this.totalRowsValidated;
    },
    totalRowsValidated() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.validated === true ? sum + 1 : sum;
      }, 0);
    },
    totalRows() {
      return this.fileUploadData.length;
    },
    hasInvalidRows() {
      return !!this.invalidCount;
    },
    invalidCount() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.isInvalidRegistrationId ||
          row.isInvalidEmailAddress ||
          row.isMissingCountryId ||
          row.isMissingStateId ||
          row.isMissingExtendedTimeReason ||
          row.isMissingFunctionId ||
          row.isMissingRegionId ||
          row.isMissingFacilityId ||
          row.isMissingCompanyId
          ? sum + 1
          : sum;
      }, 0);
    },
    hasDuplicates() {
      return !!this.duplicateCount;
    },
    duplicateCount() {
      return this.fileUploadData.reduce((sum, row) => {
        return row.isDuplicateRegistrationId || row.isDuplicateEmailAddress ? sum + 1 : sum;
      }, 0);
    },
    countryStates() {
      return this.states.filter(s => s.countryCode === this.country);
    },
    showInvestigationId() {
      var selectedTest = this.tests.find(test => test.id == this.testId);
      return selectedTest != null && selectedTest.displayName === "Investigation";
    },
    showDetailsForm() {
      return this.formWizardStep == 1;
    },
    showDemographicsForm() {
      return this.formWizardStep == 2;
    },
    companiesDisabled() {
      return this.companies.length == 0;
    },
    facilitiesDisabled() {
      return this.facilities.length == 0;
    },
    regionsDisabled() {
      return this.regions.length == 0;
    },
    functionsDisabled() {
      return this.functions.length == 0;
    },
    formatter: () => formatter,
  },
  created() {
    this.loadingMessage = "Loading...";
    const promises = [
      this.getOrganization(),
      this.getFacilities(),
      this.getRegions(),
      this.getFunctions(),
      this.getCompanies(),
      this.getTests(),
      //demographics
      this.getDemographicRaces(),
      this.getDemographicAgeGroups(),
      this.getDemographicGenders(),
      this.getDemographicEducationLevels(),
      this.getDemographicEmploymentStatuses(),
    ];
    Promise.all(promises).then(() => (this.loadingMessage = null));
  },
  methods: {
    reset() {
      this.loadingMessage = null;
      this.formWasValidated = false;
      this.error = null;
    },
    getOrganization() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}`)
        .then(res => {
          this.collectDemographicData = res.data.collectDemographicData;
          this.extendedTime = res.data.extendedTimeDefault;
          this.extendedTimeReason = res.data.extendedTimeDefault ? "Extended time defaulted by organization." : null;
        })
        .catch(err => (this.error = err));
    },
    getCompanies() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/companies`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.companies = items))
        .catch(err => (this.error = err));
    },
    getFacilities() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/facilities`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.facilities = items))
        .catch(err => (this.error = err));
    },
    getRegions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/regions`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.regions = items))
        .catch(err => (this.error = err));
    },
    getFunctions() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/functions`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.functions = items))
        .catch(err => (this.error = err));
    },
    getTests() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/tests`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => this.filterEnabled(items))
        .then(items => (this.tests = items))
        .catch(err => (this.error = err));
    },
    getDemographicRaces() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/demographics/races`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => (this.races = items))
        .catch(err => (this.error = err));
    },
    getDemographicAgeGroups() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/demographics/age-groups`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => (this.ageGroups = items))
        .catch(err => (this.error = err));
    },
    getDemographicGenders() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/demographics/genders`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => (this.genders = items))
        .catch(err => (this.error = err));
    },
    getDemographicEducationLevels() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/demographics/education-levels`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => (this.educationLevels = items))
        .catch(err => (this.error = err));
    },
    getDemographicEmploymentStatuses() {
      return this.$axios
        .get(`/v1/organizations/${this.organizationId}/demographics/employment-statuses`)
        .then(res => res.data)
        .then(items => this.sortByDisplayName(items))
        .then(items => (this.employmentStatuses = items))
        .catch(err => (this.error = err));
    },
    getReportRegistrationIdDuplicate() {
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/reports/${this.registrationId}/duplicate`)
        .then(res => {
          var data = res.data;
          if (data.count > 0 && data.previousDate) {
            this.previousDate = data.previousDate;
            $("#duplicateRegistrationModal").modal("show");
          }
        })
        .catch(err => (this.error = err));
    },
    prev() {
      this.formWizardStep--;
    },
    next() {
      // reset
      this.reset();

      // reset form validation
      this.formWasValidated = false;

      if (this.showDetailsForm) {
        // validations
        if (!this.$refs.formDetails.checkValidity()) {
          this.formWasValidated = true;
          return;
        }

        // wizard >> next form
        if (this.collectDemographicData) {
          this.formWizardStep++;
          return;
        }

        // wizard >> end
        this.submit();
      }

      if (this.showDemographicsForm) {
        // validations
        if (!this.$refs.formDemographics.checkValidity()) {
          this.formWasValidated = true;
          return;
        }

        // wizard >> end
        this.submit();
      }
    },
    submit() {
      const payload = {
        // details
        facilityId: this.facilityId,
        companyId: this.companyId,
        regionId: this.regionId,
        functionId: this.functionId,
        testId: this.testId,
        registrationId: this.registrationId,
        investigationId: this.investigationId,
        country: this.country,
        state: this.state,
        extendedTime: this.extendedTime,
        extendedTimeReason: this.extendedTimeReason,
        languageCode: this.languageCode,
        // demographics
        demographics: {
          raceCode: this.raceCode,
          ageGroupCode: this.ageGroupCode,
          genderCode: this.genderCode,
          educationLevelCode: this.educationLevelCode,
          employmentStatusCode: this.employmentStatusCode,
        },
      };

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/reports`, payload)
        .then(res => {
          var reportId = res.data.id;
          var url = res.data.url;
          this.$router.push({ name: "organization-reports-start", params: { organizationId: this.organizationId, reportId: reportId, url: url } });
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    sortByDisplayName(items) {
      return items.sort((a, b) => (a.displayName > b.displayName ? 1 : -1));
    },
    filterEnabled(items) {
      return items.filter(item => item.status === "enabled");
    },
    cancel() {
      this.$router.push(`/organizations/${this.organizationId}/reports`);
    },
    downloadSampleFile() {
      var interviewName = this.tests && this.tests.length > 0 ? this.tests[0].displayName : "Pre-Employment";

      var workbook = XLSX.utils.book_new();
      var worksheet = XLSX.utils.aoa_to_sheet([
        [
          "Registration Id",
          "Email Address",
          "Interview Type",
          "Country",
          "State",
          "Extended Time",
          "Extension Reason",
          "Language",
          "Function",
          "Region",
          "Facility",
          "Company",
        ],
        ["your-registration-id-1", "example1@example.com", interviewName, "United States", "Georgia", "yes", "Second language", "English", "", "", "", ""],
        ["your-registration-id-2", "example3@example.com", interviewName, "United States", "Georgia", "no", "", "English", "", "", "", ""],
        ["your-registration-id-3", "example2@example.com", interviewName, "Mexico", "Chihuahua", "no", "", "Spanish", "", "", "", ""],
      ]);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Interviews");

      this.createDataWorksheet(this.functions, ["Function Values"], "Function-District Values", workbook);
      this.createDataWorksheet(this.regions, ["Region Values"], "Region Values", workbook);
      this.createDataWorksheet(this.facilities, ["Facility Values"], "Facility-Store Values", workbook);
      this.createDataWorksheet(this.companies, ["Company Values"], "Company-Division Values", workbook);
      this.createDataWorksheet(this.languages, ["Language Values"], "Language Values", workbook);
      this.createDataWorksheet(this.tests, ["Interview Name Values"], "Interview Name Values", workbook);

      var countriesStates = [];
      countriesStates.push(["Country Values", "State Values"]);
      for (let i = 0; i < this.countries.length; i++) {
        for (let j = 0; j < this.states.length; j++) {
          if (this.states[j].countryCode == this.countries[i].code) {
            countriesStates.push([this.countries[i].displayName, this.states[j].displayName]);
          }
        }
      }
      var dataSheet = XLSX.utils.aoa_to_sheet(countriesStates);
      XLSX.utils.book_append_sheet(workbook, dataSheet, "Countries-States Values");

      XLSX.writeFile(workbook, "Verensics - Interviews - Sample.xlsx");
    },
    createDataWorksheet(data, header, sheet_name, workbook) {
      if (data && data.length > 0) {
        var values = data.map(x => [x.displayName]);
        values.unshift(header);
        var dataSheet = XLSX.utils.aoa_to_sheet(values);
        XLSX.utils.book_append_sheet(workbook, dataSheet, sheet_name);
      }
    },
    async onFile(e) {
      // parse
      this.fileSelected = true;
      var file = e.target.files[0];
      var fileData = await file.arrayBuffer();
      var workbook = XLSX.read(fileData);

      var dataIndex = workbook.SheetNames.findIndex(x => x == "Interviews");
      if (dataIndex >= 0) {
        // extract data
        var worksheet = workbook.Sheets["Interviews"];
        var worksheetData = XLSX.utils.sheet_to_json(worksheet);

        // collect data
        this.fileUploadData = [];
        var key = 0;
        worksheetData.forEach(r => {
          var registrationId = r["Registration Id"];
          var emailAddress = r["Email Address"];
          var testName = r["Interview Type"];
          var testId = this.getDataValueId(this.tests, testName);

          var countryName = r["Country"];
          var country = this.getDataValueCode(this.countries, countryName);

          var stateName = r["State"];
          var state = this.getDataValueCode(this.states, stateName);

          var extendedTimeName = r["Extended Time"];
          var extendedTime = extendedTimeName && extendedTimeName.toLowerCase() === "yes" ? true : false;
          var reason = r["Extension Reason"];

          var language = r["Language"];
          var languageCode = this.getDataValueId(this.languages, language);
          var functionName = r["Function"];
          var functionId = this.getDataValueId(this.functions, functionName);

          var regionName = r["Region"];
          var regionId = this.getDataValueId(this.regions, regionName);

          var facilityName = r["Facility"];
          var facilityId = this.getDataValueId(this.facilities, facilityName);

          var companyName = r["Company"];
          var companyId = this.getDataValueId(this.companies, companyName);

          if (registrationId || emailAddress) {
            this.fileUploadData.push({
              key: key++,
              registrationId: registrationId ? registrationId.toString() : "",
              emailAddress: emailAddress,
              testName: testName,
              testId: testId,
              countryName: countryName,
              country: country,
              stateName: stateName,
              state: state,
              extendedTime: extendedTime,
              extendedReason: reason,
              language: language,
              languageCode: languageCode,
              functionName: functionName,
              functionId: functionId,
              regionName: regionName,
              regionId: regionId,
              facilityName: facilityName,
              facilityId: facilityId,
              companyName: companyName,
              companyId: companyId,
              validated: false,
            });
          }
        });
      }
    },
    getDataValueId(data, value) {
      if (!value) {
        return "";
      }

      var dataIndex = data.findIndex(x => x.displayName.toLowerCase() == value.toLowerCase());
      if (dataIndex >= 0) {
        return data[dataIndex].id;
      }
      return "";
    },
    getDataValueCode(data, value) {
      if (!value) {
        return "";
      }

      var dataIndex = data.findIndex(x => x.displayName.toLowerCase() == value.toLowerCase());
      if (dataIndex >= 0) {
        return data[dataIndex].code;
      }
      return "";
    },
    async validateFile() {
      if (!this.fileUploadData || !this.fileUploadData.length) return;
      this.loadingMessage = "Validating...";
      await this.fileUploadData.reduce(async (promise, row) => {
        await promise; // wait for previous
        await new Promise(r => setTimeout(r, 250)); // throttle
        await this.validateRow(row);
      }, Promise.resolve());
      this.loadingMessage = null;
    },
    async validateRow(row) {
      row.isInvalidRegistrationId = this.isInvalidRegistrationId(row.registrationId);
      row.isInvalidEmailAddress = this.isInvalidEmailAddress(row.emailAddress);
      row.isDuplicateRegistrationId = await this.isDuplicateRegistrationId(row.registrationId);
      row.isMissingCountryId = row.country && row.country != "" ? false : true;
      row.isMissingStateId = row.state && row.state != "" ? false : true;
      row.isMissingLanguage = row.languageCode && row.languageCode != "" ? false : true;
      row.isMissingExtendedTimeReason = this.isMissingRequiredData(!row.extendedTime, row.extendedReason);
      row.isMissingTestId = this.isMissingRequiredData(false, row.testId);
      row.isMissingFunctionId = this.isMissingRequiredData(this.functionsDisabled, row.functionId);
      row.isMissingRegionId = this.isMissingRequiredData(this.regionsDisabled, row.regionId);
      row.isMissingFacilityId = this.isMissingRequiredData(this.facilitiesDisabled, row.facilityId);
      row.isMissingCompanyId = this.isMissingRequiredData(this.companiesDisabled, row.companyId);
      // force binding by updating the reference being enumerated
      this.fileUploadData = this.fileUploadData.slice();
      row.validated = true;
    },
    isMissingRequiredData(isDisabled, dataValue) {
      if (isDisabled) return false;
      if (dataValue && dataValue != "") return false;
      return true;
    },
    isInvalidRegistrationId(registrationId) {
      return !registrationId || !registrationId.trim();
    },
    isInvalidEmailAddress(emailAddress) {
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email
      var regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      return emailAddress && (!emailAddress.trim() || !regex.test(emailAddress));
    },
    async isDuplicateRegistrationId(registrationId) {
      let encodedValue = encodeURIComponent(registrationId);
      return await this.$axios.get(`/v1/organizations/${this.organizationId}/reports/${encodedValue}/duplicate`).then(res => {
        var isDuplicate = res.data.count > 0 && !!res.data.previousDate;
        return isDuplicate;
      });
    },
    getRowClass() {
      return "";
    },
    getRowStatusIcon(row) {
      if (!row.validated) return "sync";
      if (
        row.isInvalidRegistrationId ||
        row.isInvalidEmailAddress ||
        row.isMissingLanguage ||
        row.isMissingCountryId ||
        row.isMissingStateId ||
        row.isMissingExtendedTimeReason ||
        row.isMissingTestId ||
        row.isMissingFunctionId ||
        row.isMissingRegionId ||
        row.isMissingFacilityId ||
        row.isMissingCompanyId
      ) {
        return "times";
      }
      if (row.isDuplicateRegistrationId) return "exclamation-triangle";
      return "check";
    },
    getRowStatusIconClass(row) {
      if (!row.validated) return "";
      if (
        row.isInvalidRegistrationId ||
        row.isInvalidEmailAddress ||
        row.isMissingLanguage ||
        row.isMissingCountryId ||
        row.isMissingStateId ||
        row.isMissingExtendedTimeReason ||
        row.isMissingTestId ||
        row.isMissingFunctionId ||
        row.isMissingRegionId ||
        row.isMissingFacilityId ||
        row.isMissingCompanyId
      ) {
        return "text-danger";
      }
      if (row.isDuplicateRegistrationId) return "text-warning";
      return "text-success";
    },
    getRowStatusIconSpin(row) {
      return !row.validated;
    },
    getRowStatusText(row) {
      if (row.isInvalidRegistrationId) return "Invalid Registration Id";
      if (row.isInvalidEmailAddress) return "Invalid Email Address";
      if (row.isMissingLanguage) return "Invalid Language";
      if (row.isMissingCountryId) return "Invalid Country";
      if (row.isMissingStateId) return "Invalid State";
      if (row.isMissingExtendedTimeReason) return "Missing Extended Time Reason";
      if (row.isMissingTestId) return "Invalid Interview Type";
      if (row.isMissingFunctionId) return "Invalid Function";
      if (row.isMissingRegionId) return "Invalid Region";
      if (row.isMissingFacilityId) return "Invalid Facility";
      if (row.isMissingCompanyId) return "Invalid Company";
      if (row.isDuplicateRegistrationId) return "Duplicate Registration Id";
      return "";
    },
    resetBulk() {
      this.error = null;
      this.loadingMessage = null;
      this.fileSelected = false;
      this.isBadFile = false;
      this.fileUploadData = [];
    },
    submitBulk() {
      var reports = [];
      this.fileUploadData.forEach(r => {
        reports.push({
          testId: r.testId,
          investigationId: null,
          emailAddress: r.emailAddress,
          // details
          facilityId: r.facilityId,
          companyId: r.companyId,
          regionId: r.regionId,
          functionId: r.functionId,
          registrationId: r.registrationId,
          country: r.country,
          state: r.state,
          extendedTime: r.extendedTime,
          extendedTimeReason: r.extendedReason,
          languageCode: r.languageCode,
          // demographics
          demographics: {
            raceCode: "",
            ageGroupCode: "",
            genderCode: "",
            educationLevelCode: "",
            employmentStatusCode: "",
          },
        });
      });

      const payload = {
        reports: reports,
      };

      // api
      this.loadingMessage = "Saving...";
      this.$axios
        .post(`/v1/organizations/${this.organizationId}/bulkreports`, payload)
        .then(res => {
          this.$router.push({ name: "bulk-organization-reports", params: { organizationId: this.organizationId, results: res.data.results } });
        })
        .catch(err => (this.error = err))
        .finally(() => (this.loadingMessage = null));
    },
    cancelBulk() {
      this.$router.push(`/organizations/${this.organizationId}/reports`);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.span-link {
  font-weight: bold;
  text-decoration: underline;

  &:hover {
    cursor: pointer;
  }
}

.strike-through {
  text-decoration: line-through;
}
</style>
